import { useCurrentAccount, useUnseenMessages } from '@application/api';
import { useApplicationMessageModal } from '@application/components';
import { useImageLightboxState } from '@application/state';
import { useConfirmModal, useRouter, useValidation } from '@application/utils';
import type { AccountGetConversationsQuery } from '@gql/graphql';
import { ConfirmationModal, EmptyOverlay } from '@presentation/components';
import type {
  MtMessagePageConversationData,
  MtMessagePageFormValues,
  MtMessagePageMessageData,
  MtMessagePageMessageSectionData,
  MtMessagesPageProps,
} from '@presentation/pages';
import { MtMessagesPage } from '@presentation/pages';
import { PageSkeleton } from '@presentation/pages/MtMessages/components/PageSkeleton';
import { useTranslation } from '@presentation/utils';
import cleanDeep from 'clean-deep';
import dayjs from 'dayjs';
import { FormikProvider, useFormik } from 'formik';
import { groupBy } from 'lodash/fp';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDeleteConversation } from './api/useDeleteConversation';
import { useDeleteMessage } from './api/useDeleteMessage';
import { useGetConversations } from './api/useGetConversations';
import { useGetMessages } from './api/useGetMessages';
import { useSendMessage } from './api/useSendMessage';

export const MESSAGE_POLL_TIME = 10 * 1000;

export const MtMessagesPageController: FC = () => {
  const { infoFromCurrentPath, navigateToRoute } = useRouter();
  const { translate } = useTranslation();
  const { translate: translateRessource } = useTranslation({
    keyPrefix: 'mt.messages',
  });

  const [currentConversationId, setCurrentConversationId] = useState<number | undefined>(undefined);

  const urlOtherAccountId = useMemo(() => {
    const id = infoFromCurrentPath.params[0];
    return id ? Number(id) : undefined;
  }, [infoFromCurrentPath.params]);

  const { setAccountId: setMessageModalAccountId, show: showMessageModal } =
    useApplicationMessageModal();

  const [mobileMessagesShown, setMobileMessagesShown] = useState(false);
  const { conversations, conversationsLoading, converstationsStartPoll, converstationsStopPoll } =
    useGetConversations();

  const { open } = useImageLightboxState();

  const currentConversation = useMemo(() => {
    return (
      conversations.find((x) => x.otherAccountId === currentConversationId) ?? conversations[0]
    );
  }, [conversations, currentConversationId]);

  const { messages, messagesStartPoll, messagesStopPoll, messagesLoading } = useGetMessages(
    currentConversation?.otherAccountId,
  );
  const { unseenMessages } = useUnseenMessages();
  const { sendMessageFromForm, sendMessageError } = useSendMessage(
    currentConversation?.otherAccountId,
  );
  const { deleteMessage, deleteMessageError } = useDeleteMessage(
    currentConversation?.otherAccountId,
  );
  const { deleteConversation, deleteConversationLoading } = useDeleteConversation();
  const { currentAccount } = useCurrentAccount();
  const { requiredMessage } = useValidation();

  const messageError = sendMessageError || deleteMessageError;

  useEffect(() => {
    if (!urlOtherAccountId || !conversations.length) return;
    setCurrentConversationId(urlOtherAccountId);
    const foundConversation = conversations.find((x) => x.otherAccountId === urlOtherAccountId);
    if (!foundConversation && !deleteConversationLoading && !messagesLoading) {
      setMessageModalAccountId(urlOtherAccountId);
      showMessageModal();
    }
  }, [
    conversations,
    deleteConversationLoading,
    messagesLoading,
    setMessageModalAccountId,
    showMessageModal,
    urlOtherAccountId,
  ]);

  useEffect(() => {
    messagesStartPoll(MESSAGE_POLL_TIME);
    converstationsStartPoll(MESSAGE_POLL_TIME);
    return () => {
      messagesStopPoll();
      converstationsStopPoll();
    };
  }, [converstationsStartPoll, converstationsStopPoll, messagesStartPoll, messagesStopPoll]);

  const formatConversation = useCallback(
    (
      conversation: AccountGetConversationsQuery['conversations'][number],
    ): MtMessagePageConversationData => {
      return {
        id: conversation.id,
        name: conversation.otherAccountFullName,
        label: {
          content: translate(`enum.personType.${conversation.otherAccountType}`, {
            defaultValue: '',
          }),
          theme: conversation.otherAccountType === 'buyer' ? 'blue' : 'lightBlue',
        },
        image: conversation.otherAccountProfileImage ?? undefined,
        hasUnseenMessages: !!unseenMessages.filter(
          (message) => message.fromAccountId === conversation.otherAccountId,
        ).length,
      };
    },
    [translate, unseenMessages],
  );

  const messageSections = useMemo((): MtMessagePageMessageSectionData[] => {
    const messagesByDay = groupBy((x) => {
      return dayjs(x.createdAt).calendar(dayjs(), {
        sameDay: `[${translate('glossary.today')}]`,
        lastDay: `[${translate('glossary.yesterday')}]`,
        nextDay: 'DD/MM/YYYY',
        lastWeek: 'DD/MM/YYYY',
        nextWeek: 'DD/MM/YYYY',
        sameElse: 'DD/MM/YYYY',
      });
    }, messages);
    return Object.keys(messagesByDay).map((messagesByDayKey) => {
      const formattedMessages = messagesByDay[messagesByDayKey].map(
        (message) =>
          ({
            id: message.id,
            isOwn: message.fromAccountId === currentAccount?.id,
            time: dayjs(message.createdAt).format('HH:mm'),
            file: message.img,
            text: message.content,
            seen: message.seen,
            seenAt: message.seenAt ? dayjs(message.seenAt).format('dddd HH:mm') : undefined,
          } as MtMessagePageMessageData),
      );
      return {
        title: messagesByDayKey,
        isToday: messagesByDayKey === translate('glossary.today'),
        messages: formattedMessages,
      };
    });
  }, [currentAccount?.id, messages, translate]);

  const {
    modal: deleteConversationModal,
    confirmHandler: deleteConversationConfirmHandler,
    confirmedShow: deleteConversationConfirmedShow,
  } = useConfirmModal();

  const {
    modal: deleteMessageModal,
    confirmHandler: deleteMessageConfirmHandler,
    confirmedShow: deleteMessageConfirmedShow,
  } = useConfirmModal();

  const validate: MtMessagesPageProps['form']['validate'] = useCallback(
    (values: MtMessagePageFormValues) => {
      const anyValid = Object.values(values).some((value) => Boolean(value));
      if (anyValid) return {};
      return cleanDeep({
        file: !values.file ? requiredMessage : undefined,
        message: !(values.message?.trim?.() ?? '').length ? requiredMessage : undefined,
      });
    },
    [requiredMessage],
  );

  const currentConversationAccountIsDeleted = currentConversation?.otherAccountIsDeleted;

  const form = useFormik({
    initialValues: { message: '' },
    validate,
    onSubmit: async (values, helpers) => {
      if (!values.message && !values.file) return;
      sendMessageFromForm({
        ...values,
        otherAccountId: currentConversation.otherAccountId,
      });
      helpers.resetForm();
    },
  });

  return (
    <>
      <ConfirmationModal
        text={translate('common.modals.deleteConversation.title').replace(
          '$name',
          currentConversation?.otherAccountFullName ?? '',
        )}
        modal={deleteConversationModal}
        onConfirm={deleteConversationConfirmHandler}
      />
      <ConfirmationModal
        text={translate('common.modals.deleteMessage.title')}
        modal={deleteMessageModal}
        onConfirm={deleteMessageConfirmHandler}
      />
      {conversationsLoading && <PageSkeleton />}
      {!conversationsLoading && !currentConversation && (
        <EmptyOverlay title={translateRessource('empty')} />
      )}
      {!conversationsLoading && !!currentConversation && (
        <FormikProvider value={form}>
          <MtMessagesPage
            messageError={messageError}
            messageSections={messageSections}
            conversations={conversations.map((x) => formatConversation(x))}
            currentConversation={formatConversation(currentConversation)}
            onConversationClick={(index) => {
              navigateToRoute('mtMessages', {
                params: [conversations[index].otherAccountId.toString()],
              });
              setMobileMessagesShown(true);
            }}
            loading={conversationsLoading}
            mobileMessagesShown={mobileMessagesShown}
            mobileOnBackClick={() => setMobileMessagesShown(false)}
            onDeleteMessage={async (messageId) => {
              const confirmed = await deleteMessageConfirmedShow();
              if (!confirmed) return;
              await deleteMessage(messageId);
              navigateToRoute('mtMessages');
            }}
            onDeleteConversation={async (conversationId) => {
              const confirmed = await deleteConversationConfirmedShow();
              if (!confirmed) return;
              deleteConversation(conversationId);
              navigateToRoute('mtMessages');
            }}
            onImageClick={(url: string) => {
              open([url]);
            }}
            otherAccountIsDeleted={currentConversationAccountIsDeleted}
          />
        </FormikProvider>
      )}
    </>
  );
};
